/**
 * DSD 드래그 다중 선택 → 매칭 IXD 셀로 스크롤 + IXD 패널 외곽선용 대응 목록.
 *
 * `useIxdDsdCounterpart` 의 반대 방향이고 구조도 같다 — 선택 상태·매칭 결과·스크롤을
 * 한 훅이 소유해 라이브와 다운로드 뷰어가 배선을 각자 다시 짜지 않게 한다.
 *
 * 단일 클릭은 navigation(focusByCellIds)이 스크롤을 담당하지만, 드래그 다중 선택은
 * counterpart 만 전달돼 IXD 가 제자리에 남아 있었다. 강조는 counterpart 스타일이
 * 하므로 여기서는 스크롤만 한다(focus ring 은 다중 선택에서 꺼져 있음).
 *
 * 스크롤 방식은 단일 클릭과 같은 `planFocusScroll` 을 쓴다 — 같은 셀을 클릭으로 가느냐
 * 드래그로 가느냐에 따라 화면이 다르게 움직이면 안 된다.
 *
 * 후보를 좁히는 기준은 **백엔드가 준 정본 셀의 DL·기간**이다(`mostProperCell`). DSD
 * 값의 배정은 그 IXD 셀이 어디 있는지에 대한 추정일 뿐이고, 고른 쪽만 실제 위치를 안다.
 * 정본이 없으면(구버전 산출물) 좁히지 않는다 — 아티팩트 버전 bump 로 조회 시 재대사가
 * 돌아 스스로 회복하므로, 그 한 요청을 위해 DSD 배정을 따로 들고 다니지 않는다.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import type { DSDSubChunkInfo } from "@/apis/compare/route";
import type { IxdPreviewRendererRef } from "@/components/ixd-preview";
import {
  preferCellsInDlView,
  preferCellsInPeriod,
} from "@/hooks/ixd-dsd-compare/use-ixd-focus";
import { periodOfCellId } from "@/utils/ixd-dsd-compare/cell-id-selector";
import {
  buildIxdByDsdCellIdMap,
  splitRefsForCounterpart,
  type IxdMatchRef,
} from "@/utils/ixd-dsd-compare/build-ixd-by-dsd-cell-id";
import {
  applyFocusScroll,
  IXD_SECTION_SELECTOR,
  planAlignedScroll,
  planFocusScroll,
} from "@/utils/ixd-dsd-compare/focus-utils";

const NO_COUNTERPART = { cellIds: [], identityKeys: [] };

interface Params {
  subChunks: DSDSubChunkInfo[] | undefined | null;
  ixdRendererRef: React.RefObject<IxdPreviewRendererRef>;
  /** 값이 바뀌면 선택을 비운다(청크 전환·편집 모드 토글 등 stale 선택이 남는 자리). */
  resetKey?: string | null;
  /** 편집 모드 등 이 동선 전체(스크롤·강조)를 끄고 싶을 때. */
  disabled?: boolean;
}

/**
 * DSD 드래그 선택을 받아 대응 IXD 셀로 스크롤하고, 그 대응 목록을 돌려준다.
 *
 * 반대 방향 `useIxdDsdCounterpart` 와 대칭이지만 **네 군데가 다르다.** 한쪽을 고칠 때
 * 다른 쪽에 그대로 옮기면 안 되는 지점들이다.
 *
 * 1. **드래그 2개 이상만 받는다.** 단일 클릭은 `useNavigation`(focusByCellIds)이 스크롤
 *    까지 소유하므로, DSD 렌더러가 단일 선택에는 빈 목록을 보낸다. 여기서 단일 클릭을
 *    다시 처리하면 같은 셀에 스크롤이 두 번 걸린다.
 * 2. **조회가 한 단계다.** dsdId 는 유일해 맵을 바로 친다. 축 구분은 조회가 아니라
 *    조회 **결과**(`IxdMatchRef.identityKey`)로 하며, 그 구분은 스크롤 대상 셀을 좁히는
 *    데 쓴다.
 * 3. **스크롤을 직접 한다.** IXD 렌더러엔 위임할 핸들이 없어 `planFocusScroll` 을
 *    여기서 부른다 — 단일 클릭과 같은 함수를 써야 클릭·드래그가 같은 화면을 만든다.
 * 4. **중복 스크롤을 막아야 한다.** 드래그는 같은 목적지를 가리키는 이벤트를 연속으로
 *    쏟아낸다. `lastScrolledRef` 가 그걸 거르며, 반대 방향엔 필요 없는 장치다.
 *
 * @param subChunks - 현재 DSD 청크. 여기서 정방향 매칭 맵을 만든다.
 * @param ixdRendererRef - 셀 검색·스크롤 컨테이너 조회에 쓰는 IXD 렌더러 핸들.
 * @param resetKey - 값이 바뀌면 선택을 비운다(청크 전환·편집 토글).
 * @param disabled - 스크롤과 강조를 함께 끈다(편집 모드).
 * @returns `onDsdCellIdsChange` 를 DSD 패널의 `onSelectedDsdCellIdsChange` 에,
 *   `ixdCounterpart` 를 IXD 패널의 `counterpartCellIds`/`counterpartIdentityKeys` 에 편다.
 */
export const useDsdIxdCounterpart = ({
  subChunks,
  ixdRendererRef,
  resetKey,
  disabled,
}: Params) => {
  const ixdByDsdCellId = useMemo(
    () => buildIxdByDsdCellIdMap(subChunks),
    [subChunks],
  );

  const [selectedDsdIds, setSelectedDsdIds] = useState<string[]>([]);

  // 드래그 중 selection change 가 연속으로 오므로 같은 목적지로는 다시 스크롤하지 않는다.
  const lastScrolledRef = useRef<HTMLElement | null>(null);

  // resetKey 가 바뀐 렌더에서 곧바로 비운다 — 이펙트로 미루면 그 프레임의 대응 목록이
  // 이전 선택으로 계산돼 지금 표와 무관한 외곽선이 한 번 보인다.
  //
  // "직전에 간 곳" 도 같이 버린다. 이전 청크의 곧 detached 될 노드를 붙잡고 있을 뿐
  // 아니라, 렌더러가 재마운트되지 않는 리셋(편집 모드 토글)에서는 같은 노드가 그대로
  // 남아 있어 리셋 후 그 셀을 다시 골라도 아래 스킵에 걸려 영영 스크롤되지 않는다.
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey);
    setSelectedDsdIds([]);
    lastScrolledRef.current = null;
  }

  /** 축 충돌 구분: identityKey 가 있는 cellId 는 그 identity 의 셀로만 좁힌다. */
  const searchRefs = useCallback(
    (refs: IxdMatchRef[]) => {
      const identityKeysByCellId = new Map<string, string[]>();
      for (const ref of refs) {
        if (!ref.identityKey) continue;
        const keys = identityKeysByCellId.get(ref.cellId) ?? [];
        keys.push(ref.identityKey);
        identityKeysByCellId.set(ref.cellId, keys);
      }
      return (
        ixdRendererRef.current?.searchByCellIds(
          refs.map((ref) => ref.cellId),
          identityKeysByCellId.size > 0 ? identityKeysByCellId : undefined,
        ) ?? []
      );
    },
    [ixdRendererRef],
  );

  const onDsdCellIdsChange = useCallback(
    /**
     * @param options.alignWith - 주면 대응 IXD 셀을 이 엘리먼트와 같은 화면 높이에 맞춘다
     *   (값 위치 검토). 같은 셀이어도 다시 맞춘다 — DSD 쪽이 움직였을 수 있다.
     */
    (dsdIds: string[], options?: { alignWith?: HTMLElement | null }) => {
      setSelectedDsdIds(dsdIds);

      if (disabled || dsdIds.length === 0) {
        lastScrolledRef.current = null;
        return;
      }
      for (const dsdId of dsdIds) {
        const matched = ixdByDsdCellId.get(dsdId);
        if (!matched?.refs.length) continue;
        const { refs, proper } = matched;
        // 정본 매칭이 목적지고, 그 셀이 화면에 없을 때만 매칭 목록으로 물러선다 —
        // 목록의 첫 셀은 문서 순서일 뿐이라 클릭으로 간 곳과 달라진다.
        //
        // 검색 결과는 클릭 경로와 같은 필터를 태운다. cellId 도 identity 도 DL 을
        // 담지 않아 같은 fact 를 태깅한 두 DL 의 셀이 함께 잡히기 때문이다.
        const pickCell = (candidates: IxdMatchRef[]) =>
          preferCellsInPeriod(
            preferCellsInDlView(
              searchRefs(candidates),
              proper?.dlViewId ?? null,
            ),
            proper ? periodOfCellId(proper.cellId) : null,
          )[0];
        const properRef = refs.find((ref) => ref.isMostProper);
        const cell = (properRef && pickCell([properRef])) ?? pickCell(refs);
        if (!cell) continue;
        const alignWith = options?.alignWith ?? null;
        if (!alignWith && lastScrolledRef.current === cell) return;
        // 컨테이너 조회가 ref 갱신보다 **앞**이어야 한다 — 렌더러 마운트 전이면
        // `getScrollContainer()` 가 null 이라 스크롤은 못 하는데 ref 에는 "여기로
        // 갔다" 가 남고, 같은 셀을 가리키는 다음 이벤트가 위에서 걸러져 그 셀로는
        // 영영 스크롤되지 않는다.
        const container = ixdRendererRef.current?.getScrollContainer();
        if (!container) return;
        const prevCell = lastScrolledRef.current;
        lastScrolledRef.current = cell;
        applyFocusScroll(
          alignWith
            ? planAlignedScroll({
                cell,
                container,
                alignTop: alignWith.getBoundingClientRect().top,
              })
            : planFocusScroll({
                cell,
                prevCell,
                container,
                sectionSelector: IXD_SECTION_SELECTOR,
              }),
        );
        return;
      }
    },
    [disabled, ixdByDsdCellId, ixdRendererRef, searchRefs],
  );

  /**
   * IXD 패널 외곽선 대상. identityKey 가 있는 매칭은 identity 로 강조해 동일 cellId 의
   * 다른 축 셀이 칠해지지 않게 한다.
   *
   * 외곽선만 그리고 dim 을 만들지 않으므로 "꺼짐"과 "매칭 없음"을 구분할 필요가 없다 —
   * 둘 다 빈 배열이면 아무 외곽선도 안 그려지고 그게 맞는 화면이다. 반대 방향
   * (`useIxdDsdCounterpart`)이 `Set | null` 3상태를 쓰는 이유가 여기서 갈린다.
   */
  const ixdCounterpart = useMemo(() => {
    if (disabled || selectedDsdIds.length === 0) return NO_COUNTERPART;
    return splitRefsForCounterpart(
      selectedDsdIds.flatMap((dsdId) => ixdByDsdCellId.get(dsdId)?.refs ?? []),
    );
  }, [disabled, selectedDsdIds, ixdByDsdCellId]);

  return { onDsdCellIdsChange, ixdCounterpart };
};
